import React, { useMemo } from 'react';
import { ChartPie } from './ChartPie';
import { ChartPieProductExtendedProps, DataRow, Item } from "./types";
import { Box, Typography } from '@mui/material';
import { transformData, useGetFilteredItemsByClass, convertUnits } from "./Chart.Common";

const productBuildLabels = (data: any[]): string[] => {
    return data.map((row) => {
        let label = "";
        if (row.NAME) {
            label += `${row.NAME}\n`;
        }
        if (row.LOD) {
            label += `LOD: ${row.LOD}, `;
        }
        if (row.LOQ) {
            label += `LOQ: ${row.LOQ}\n`;
        }
        if (row.NUMBER_OF_ITEMS_PER_REPLICATE) {
            label += `Items per Replicate: ${row.NUMBER_OF_ITEMS_PER_REPLICATE}\n`;
        }
        if (row.NUMBER_OF_REPLICATES) {
            label += `Number of Replicates: ${row.NUMBER_OF_REPLICATES}\n`;
        }
        if (row.STD_DEV) {
            label += `STDEV: ${row.STD_DEV}\n`;
        }
        return label.slice(0, -2); // Remove trailing comma after LOQ
    });
};

const ChartPieProduct: React.FC<ChartPieProductExtendedProps> = ({
                                                                     data,
                                                                     selectedProducts,
                                                                     selectedClass,
                                                                     compoundFilter,
                                                                     showNonNumeric,
                                                                     selectedUnit,
                                                                     selectedDenominator,
                                                                     diseaseInchiKeys,
                                                                     selectedCompounds
                                                                 }) => {
    const filteredItems = useMemo(() =>
            data.items.filter((item: Item<DataRow>) => item.HEADER === "List of Products Analysis Values"),
        [data.items]
    );

    // Convert units
    const convertedItems = useMemo(() =>
            convertUnits(filteredItems, selectedUnit, selectedDenominator),
        [filteredItems, selectedUnit, selectedDenominator]
    );

    // Filter by class
    const classFilteredItems = useGetFilteredItemsByClass(convertedItems, selectedClass);

    const processedItems = useMemo(() => {
        if (selectedProducts.length === 0) return classFilteredItems;

        const productItems: any[] = [];
        classFilteredItems.forEach(item => {
            const productDataMap = new Map<string, any[]>();
            item.DATA.forEach((row: any) => {
                const key = `${row.PRODUCT || 'Unknown'} (${row.ANALYSIS_TYPE || 'Unknown'})`;
                if (selectedProducts.includes(key)) {
                    if (!productDataMap.has(key)) {
                        productDataMap.set(key, []);
                    }
                    productDataMap.get(key)!.push(row);
                }
            });

            productDataMap.forEach((data, key) => {
                productItems.push({
                    ...item,
                    DATA: data,
                    TITLE: key
                });
            });
        });

        console.log('Processed Items:', productItems);
        return productItems;
    }, [classFilteredItems, selectedProducts]);

    // Transform data
    const chartData = useMemo(() => {
        const transformed = transformData(
            processedItems,
            selectedProducts,
            'direct',
            (item) => `${item.DATA[0]?.PRODUCT || 'Unknown'} (${item.DATA[0]?.ANALYSIS_TYPE || 'Unknown'})`,
            (row) => Number(row.AVERAGE) || 0,
            (row) => row.COMPOUND_NAME,
            (row) => Number(row.STD_DEV) || null,
            (item) => item.DATA[0]?.UNIT || `${selectedUnit}/${selectedDenominator}`,
            compoundFilter,
            showNonNumeric,
            diseaseInchiKeys
        );

        // Apply compound filter
        if (selectedCompounds.length > 0 && !selectedCompounds.includes('All')) {
            return {
                ...transformed,
                labels: transformed.labels.filter(label => selectedCompounds.includes(label)),
                datasets: transformed.datasets.map(dataset => ({
                    ...dataset,
                    data: dataset.data.filter((_, idx) => selectedCompounds.includes(transformed.labels[idx])),
                    extras: Object.fromEntries(
                        Object.entries(dataset.extras || {}).filter(([compound]) => selectedCompounds.includes(compound))
                    )
                }))
            };
        }
        return transformed;
    }, [processedItems, selectedProducts, compoundFilter, showNonNumeric, selectedUnit, selectedDenominator, diseaseInchiKeys, selectedCompounds]);

    console.log(' ----------- chartData ', chartData);

    // Compute pieData for each dataset
    const pieDataArray = useMemo(() =>
            chartData.datasets.map((dataset, index) => {
                const productKey = dataset.label;
                const processedItem = processedItems.find((item: any) =>
                    item.TITLE === productKey
                );
                if (!processedItem) return null;

                const pieData = chartData.labels
                    .map((compound, idx) => {
                        const value = dataset.data[idx];
                        const extra = dataset.extras?.[compound];
                        if (value <= 0) return null;
                        const rowData = processedItem.DATA.find((row: DataRow) => row.COMPOUND_NAME === compound);
                        return {
                            NAME: compound,
                            VALUE: value,
                            LOQ: rowData?.LOQ ?? 'N/A',
                            LOD: rowData?.LOD ?? 'N/A',
                            NUMBER_OF_ITEMS_PER_REPLICATE: rowData?.NUMBER_OF_ITEMS_PER_REPLICATE ?? 'N/A',
                            NUMBER_OF_REPLICATES: rowData?.NUMBER_OF_REPLICATES ?? 'N/A',
                            STD_DEV: extra?.stdev ?? 'N/A',
                            UNIT: extra?.unit ?? processedItem.DATA[0]?.UNIT ?? `${selectedUnit}/${selectedDenominator}`,
                            ORIGINAL_VALUE: extra?.originalValue ?? 'N/A'
                        };
                    })
                    .filter((data): data is NonNullable<typeof data> => data !== null);

                return { processedItem, pieData, index };
            }).filter((entry): entry is NonNullable<typeof entry> => entry !== null),
        [chartData, processedItems, selectedUnit, selectedDenominator]
    );

    return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                {pieDataArray.map(({ processedItem, pieData, index }) => {
                    console.log(`Pie Data for ${processedItem.TITLE || processedItem._id || 'Unknown'}:`, pieData);

                    if (pieData.length === 0) {
                        return (
                            <Box key={processedItem._id || index} sx={{ maxWidth: '300px', minWidth: '200px', m: 1, p: 1 }}>
                                <Typography variant="h6">No data for {processedItem.TITLE || 'Unknown'}</Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box key={processedItem._id || index} sx={{ maxWidth: '300px', minWidth: '200px', m: 1, p: 1 }}>
                            <Typography variant="h6" sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                {processedItem.DATA[0]?.ANALYSIS_TYPE}
                            </Typography>
                            <Typography variant="h6" sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                {processedItem.DATA[0]?.PRODUCT}
                            </Typography>
                            <Typography variant="h6" sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                {processedItem.DATA[0]?.TEST_METHOD_CODE}
                            </Typography>
                            <Typography variant="h6" sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                {processedItem.DATA[0]?.REPORT_PROJECT_ID}
                            </Typography>
                            <ChartPie data={pieData} buildLabels={productBuildLabels} />
                            <Typography variant="h6">[ {processedItem.DATA[0]?.UNIT} ]</Typography>
                        </Box>
                    );
                })}
            </div>
        </Box>
    );
};

export default ChartPieProduct;
